import React, { useState } from "react";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import { styled } from "@mui/material/styles";
import "react-phone-input-2/lib/material.css";
import { Helmet } from "react-helmet";
import { v4FlatWinsAtBreakpointsSx } from "../theme/v4CompatSx";
import DefaultLayout from "../layouts/DefaultLayout";
import EmailSignIn from "./SignIn/EmailSignIn";
import MobileSignIn from "./SignIn/MobileSignIn";

/**
 * PM5 T2 (money path, batch "signin") — converted from MUI v4 + `makeStyles`
 * to MUI v5+ (`@mui/material`) `styled`/`sx`.
 * See docs/epics/pm5-mui-v5-migration.md §3 (T2) and docs/mui-conversion-guide.md.
 *
 * **AUTH ESCALATION CLASS.** This is the /login page shell. The conversion is
 * styling-only: no handler, state value, prop, condition or DOM node moved.
 * Acceptance bar is visual parity — every rule below is the v4 rule it
 * replaces, and each place a v5+ library default would have changed the
 * rendering is compensated for with a comment saying why.
 *
 * DEAD RULES DROPPED (guide §1.2 row 1). The v4 `useStyles` object declared 22
 * keys, of which exactly four were ever referenced from the JSX
 * (`background`, `signInContainer`, `paper`, `formHeader`). The other 18 —
 * form, submit, secSubmit, formSubHeader, thirdHeader, divider, bodyText,
 * bodySml, phoneInput, center, validationError, backdrop, forgotPassword,
 * button, secButton, formField, verificationCodeContainer,
 * submittedMobileNumber — are stale copies of rules that live in
 * EmailSignIn/MobileSignIn and were never applied to an element on this page,
 * so they rendered nothing. Carrying them forward would be inventing styles,
 * not porting them.
 */

/* ---------------------------------------------------------------------- *
 * styled() — host elements that carry a fixed style
 * ---------------------------------------------------------------------- */

// was: useStyles().background — a bare <div>, which has no `sx` prop.
// The literal `@media (min-width:600px)` becomes a breakpoint helper; themeV5
// pins the v4 breakpoint values, so the emitted CSS text is identical.
const Background = styled("div")(({ theme }) => ({
  position: "relative",
  backgroundImage: "none",
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
  backgroundSize: "cover",
  backgroundAttachment: "scroll",
  width: "100%",
  height: "auto",
  display: "flex",
  flexGrow: 1,
  boxShadow: "none",
  backgroundColor: theme.palette.background.ffNavy,
  [theme.breakpoints.up("sm")]: {
    backgroundImage: `url(https://farmflix-images-prod.s3.eu-west-1.amazonaws.com/usemycode_bg.jpg)`,
    boxShadow: "inset 4000px 0 0 0 rgba(8, 5, 5, 0.6)",
  },
}));

/* ---------------------------------------------------------------------- *
 * sx — styles on MUI components
 * ---------------------------------------------------------------------- */

/**
 * ♦ v4's `<Grid container spacing={2}>` was implemented as
 *   `.MuiGrid-spacing-xs-2      { margin: -8px; width: calc(100% + 16px) }`
 *   `.MuiGrid-spacing-xs-2 > .MuiGrid-item { padding: 8px }`
 * v9 replaced that with `gap` on the container and **no** padding on the items
 * (guide §2, "Grid `spacing` implementation"). For a container that carries no
 * box styling of its own the two are geometrically identical — but this one
 * does (`paperSx`: 48/32px padding and a tinted background), and there the two
 * differ twice over: the tinted panel would stop overhanging its parent by 8px
 * each side, and the first/last row would lose the 8px the item padding used
 * to add inside the 48px container padding.
 *
 * So the port carries v4's own declarations verbatim rather than re-deriving
 * the geometry: `spacing` is omitted (v9 default 0 ⇒ `gap: 0px`) and each item
 * carries the 8px padding itself. Identical by construction, not by arithmetic.
 */
const v4GridItemSx = { padding: "8px" };

// was: useStyles().signInContainer
//
// PARITY RESTORE (guide §2, "A flat `sx` declaration loses to the component's
// own breakpoint rule"): `padding: 0` has to be re-stated at `up("sm")`.
// `Container` sets `padding-left/right: 24px` there, and in v9 that @media
// block is emitted AFTER this class's flat declarations, so the flat
// `padding: 0` no longer cancels the gutters above 600px — measured as the
// login card losing 48px of width (460px → 412px). In v4 the makeStyles sheet
// simply had a later JSS index and won at every width.
const signInContainerSx = (theme) =>
  v4FlatWinsAtBreakpointsSx(
    theme,
    {
      width: "100%",
      padding: 0,
      margin: 0,
      [theme.breakpoints.up("sm")]: {
        margin: "0 auto",
      },
    },
    ["padding"],
    ["sm"],
  );

// was: useStyles().paper, merged with the container half of v4's `spacing={2}`
// (see `v4GridItemSx` above).
//
// Precedence note: `useStyles().paper` took a later JSS sheet index than MUI's
// own Grid styles (an imported module always evaluates first), so paper's
// `marginTop`/`marginBottom` longhands overrode the top and bottom halves of
// Grid's `margin: -8px` shorthand while the left/right halves survived. That is
// why only `marginLeft`/`marginRight` are negative here. Don't "tidy" this into
// a single `margin`.
const paperSx = (theme) => ({
  width: "calc(100% + 16px)",
  marginLeft: "-8px",
  marginRight: "-8px",
  borderRadius: "0px",
  marginTop: 0,
  marginBottom: 0,
  display: "flex",
  flexDirection: "column",
  paddingTop: 6,
  paddingBottom: 6,
  paddingLeft: 4,
  paddingRight: 4,
  // `none` is not a <color>, so this declaration was invalid — and inert — in
  // v4 as well. Kept verbatim because removing it is a tidy-up, not a port.
  backgroundColor: "none",
  [theme.breakpoints.up("sm")]: {
    backgroundColor: "rgba(23,23,23,0.9)",
    marginTop: 6,
    marginBottom: 6,
  },
});

// was: useStyles().formHeader
const formHeaderSx = (theme) => ({
  color: "common.white",
  fontSize: "2.2rem",
  textAlign: "center",
  marginBottom: 2,
  fontWeight: "600",
  [theme.breakpoints.up("sm")]: {
    fontSize: "2.2rem",
    lineHeight: "2.4rem",
  },
  [theme.breakpoints.up("md")]: {
    fontSize: "2.6rem",
    lineHeight: "2.8rem",
  },
});

const SignIn = (props) => {
  const [showEmailPasswordSignIn, setShowEmailPasswordSignIn] = useState(true);
  const [showMobileSignIn, setShowMobileSignIn] = useState(false);

  const showEmailPasswordSignInForm = () => {
    setShowMobileSignIn(false);
    setShowEmailPasswordSignIn(true);
  };

  const showMobileSignInForm = () => {
    setShowEmailPasswordSignIn(false);
    setShowMobileSignIn(true);
  };

  return (
    <DefaultLayout>
      <Helmet>
        <meta charSet="utf-8" />
        <title>FarmFLiX.tv | Sign In Now</title>
        <meta
          name="description"
          content="Sign In Now to view the Latest FarmFLiX Content."
        />
      </Helmet>
      <Background>
        <Container maxWidth="xs" sx={signInContainerSx}>
          {/* v5+ Grid: the `item` prop is gone and the breakpoint props are
              collapsed into `size`. `spacing` is deliberately omitted — see
              `v4GridItemSx`. */}
          <Grid container sx={paperSx}>
            <Grid size={{ xs: 12, sm: 12, md: 12 }} sx={v4GridItemSx}>
              <Typography component="h1" variant="h1" sx={formHeaderSx}>
                Log In
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 12 }} sx={v4GridItemSx}>
              {showEmailPasswordSignIn ? (
                <EmailSignIn toggleLoginMethod={showMobileSignInForm} />
              ) : showMobileSignIn ? (
                <MobileSignIn toggleLoginMethod={showEmailPasswordSignInForm} />
              ) : null}
            </Grid>
          </Grid>
        </Container>
      </Background>
    </DefaultLayout>
  );
};

export default SignIn;
